/**
 * Money is bigint cents everywhere (core-data-model Q5). Rounding policy, fixed here so it
 * is bit-identical across quotes, invoices, and the future QBO push (penny-drift becomes
 * structurally impossible on our side):
 *   - per line:  round_half_up(qty × unit_price_cents)
 *   - tax:       round_half_up(taxable_subtotal × tax_rate_bps / 10000)
 *   - subtotal:  sum of stored line totals  (never recomputed from scratch elsewhere)
 *   - total:     subtotal + tax
 */

/** Round half-up by magnitude (symmetric for negative discount lines). */
export function roundHalfUp(n: number): number {
  return Math.sign(n) * Math.round(Math.abs(n));
}

/** One line's total in cents: qty (decimal, e.g. 2.5) × unit price (cents). */
export function computeLineTotal(qty: number, unitPriceCents: number): number {
  return roundHalfUp(qty * unitPriceCents);
}

export interface TotalsLine {
  totalCents: number;
  taxable: boolean;
}

export interface Totals {
  subtotalCents: number;
  taxableSubtotalCents: number;
  taxCents: number;
  totalCents: number;
}

export function computeTotals(lines: TotalsLine[], taxRateBps: number): Totals {
  const subtotalCents = lines.reduce((sum, l) => sum + l.totalCents, 0);
  const taxableSubtotalCents = lines
    .filter((l) => l.taxable)
    .reduce((sum, l) => sum + l.totalCents, 0);
  const taxCents = roundHalfUp((taxableSubtotalCents * taxRateBps) / 10000);
  return {
    subtotalCents,
    taxableSubtotalCents,
    taxCents,
    totalCents: subtotalCents + taxCents,
  };
}

/** Display a cents amount as USD, e.g. 123456 → "$1,234.56". */
export function formatCents(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    cents / 100,
  );
}
